/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { LoadingButton } from '@mui/lab';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  FormCancelButton,
  FormDialogActions,
  FormElement,
  FormInputsStack,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { TicketSeverity } from '@marageti/z4-sdk/lib/tickets';
import SeverityIcon from '../ticket-severity-icon/ticket-severity-icon';

type SeverityOption = {
  label: string;
  value: TicketSeverity;
  description?: string;
};

export type TicketCloseFormData = {
  outcomeSeverity: SeverityOption | null;
  outcomeComments: string;
  zicassoCreditEnabled: boolean;
  zicassoCreditAmount: string;
  partnerRefundEnabled: boolean;
  partnerRefundAmount: string;
};

type TicketCloseModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: TicketCloseFormData) => Promise<void>;
};

const SEVERITY_OPTIONS: SeverityOption[] = [
  { label: 'No Issue', value: 'NO_ISSUE', description: 'No impact on service delivery, trip experience, or traveler-company relationship.' },
  { label: 'Minor Issue', value: 'MINOR', description: 'Limited impact on service, trip, or relationship; resolved with no lasting effects.' },
  { label: 'Major Issue', value: 'MAJOR', description: 'Serious impact on service, trip, or relationship requiring active intervention or escalation.' },
  { label: 'Critical Issue', value: 'CRITICAL', description: 'Severe service failure, trip disruption, or relationship breakdown with risk of irreversible impact.' },
];

const ticketCloseSchema = z.object({
  outcomeSeverity: z.object({
    label: z.string().min(1),
    value: z.string().min(1),
  }).nullable().refine((val) => val !== null, { message: 'Outcome is required' }),
  outcomeComments: z.string().min(1, 'Comments are required'),
  zicassoCreditEnabled: z.boolean(),
  zicassoCreditAmount: z.string(),
  partnerRefundEnabled: z.boolean(),
  partnerRefundAmount: z.string(),
}).superRefine((data, ctx) => {
  if (data.zicassoCreditEnabled && !data.zicassoCreditAmount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Credit amount is required',
      path: ['zicassoCreditAmount'],
    });
  }
  if (data.partnerRefundEnabled && !data.partnerRefundAmount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Refund amount is required',
      path: ['partnerRefundAmount'],
    });
  }
});

const TicketCloseModal = ({
  open,
  onClose,
  onSave,
}: TicketCloseModalProps) => {
  const [zicassoCreditEnabled, setZicassoCreditEnabled] = useState(false);
  const [partnerRefundEnabled, setPartnerRefundEnabled] = useState(false);

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    reset,
    setValue,
  } = useForm<TicketCloseFormData>({
    resolver: zodResolver(ticketCloseSchema),
    defaultValues: {
      outcomeSeverity: null,
      outcomeComments: '',
      zicassoCreditEnabled: false,
      zicassoCreditAmount: '',
      partnerRefundEnabled: false,
      partnerRefundAmount: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    reset({
      outcomeSeverity: null,
      outcomeComments: '',
      zicassoCreditEnabled: false,
      zicassoCreditAmount: '',
      partnerRefundEnabled: false,
      partnerRefundAmount: '',
    });
    setZicassoCreditEnabled(false);
    setPartnerRefundEnabled(false);
  }, [open]); // intentionally reset only when modal opens/closes

  const onSubmit: SubmitHandler<TicketCloseFormData> = async (data) => {
    await onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Mark as Closed</DialogTitle>
      <FormElement name="ticket-close-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 0 }}>
          <FormInputsStack>
            <Typography variant="body" color="text.secondary" sx={{ mb: 2 }}>
              Confirm that all actions are complete and no further follow-up is required.
              Please select the final outcome to continue:
            </Typography>

            <Controller
              control={control}
              name="outcomeSeverity"
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  id="ticket-close-severity"
                  getOptionLabel={(option) => option.label}
                  getOptionKey={(option) => option.value}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  options={SEVERITY_OPTIONS}
                  onChange={(_, value) => field.onChange(value)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.5,
                      }}
                      >
                        <SeverityIcon severity={option.value} size={16} />
                        <Box>
                          <Typography variant="body">
                            {option.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="p">
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Outcome"
                      required
                      error={!!errors.outcomeSeverity}
                      helperText={
                        (errors.outcomeSeverity as { message?: string } | undefined)?.message
                      }
                    />
                  )}
                  value={field.value}
                />
              )}
            />

            <Controller
              control={control}
              name="outcomeComments"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Comments"
                  required
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.outcomeComments}
                  helperText={errors.outcomeComments?.message}
                  sx={{ my: 2 }}
                />
              )}
            />

            <Box
              sx={{
                borderRadius: 2,
                p: 2,
                bgcolor: (zicassoCreditEnabled || partnerRefundEnabled) ? 'common.warning10' : 'grey.100',
              }}
            >
              <FormControlLabel
                control={(
                  <Switch
                    checked={zicassoCreditEnabled}
                    onChange={(e) => {
                      setZicassoCreditEnabled(e.target.checked);
                      setValue('zicassoCreditEnabled', e.target.checked, { shouldValidate: true });
                      if (!e.target.checked) setValue('zicassoCreditAmount', '', { shouldValidate: true });
                    }}
                  />
                )}
                label="Traveler Credit - Issued by Zicasso"
                sx={{ width: '100%', justifyContent: 'space-between', ml: 0 }}
                labelPlacement="start"
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={partnerRefundEnabled}
                    onChange={(e) => {
                      setPartnerRefundEnabled(e.target.checked);
                      setValue('partnerRefundEnabled', e.target.checked, { shouldValidate: true });
                      if (!e.target.checked) setValue('partnerRefundAmount', '', { shouldValidate: true });
                    }}
                  />
                )}
                label="Traveler Refund - Issued by Partner"
                sx={{ width: '100%', justifyContent: 'space-between', ml: 0 }}
                labelPlacement="start"
              />

              {zicassoCreditEnabled && (
                <Controller
                  control={control}
                  name="zicassoCreditAmount"
                  render={({ field }) => (
                    <NumericFormat
                      value={field.value}
                      onValueChange={(values) => field.onChange(values.value)}
                      customInput={TextField}
                      label="Credit Amount (USD)"
                      required
                      fullWidth
                      prefix="$"
                      thousandSeparator=","
                      decimalScale={2}
                      fixedDecimalScale
                      allowNegative={false}
                      valueIsNumericString
                      error={!!errors.zicassoCreditAmount}
                      helperText={errors.zicassoCreditAmount?.message}
                      sx={{
                        mt: 2,
                      }}
                    />
                  )}
                />
              )}

              {partnerRefundEnabled && (
                <Controller
                  control={control}
                  name="partnerRefundAmount"
                  render={({ field }) => (
                    <NumericFormat
                      value={field.value}
                      onValueChange={(values) => field.onChange(values.value)}
                      customInput={TextField}
                      label="Refund Amount (USD)"
                      required
                      fullWidth
                      prefix="$"
                      thousandSeparator=","
                      decimalScale={2}
                      fixedDecimalScale
                      allowNegative={false}
                      valueIsNumericString
                      error={!!errors.partnerRefundAmount}
                      helperText={errors.partnerRefundAmount?.message}
                      sx={{
                        mt: 2,
                      }}
                    />
                  )}
                />
              )}
            </Box>
          </FormInputsStack>
        </DialogContent>

        <FormDialogActions sx={{ mt: 0, border: 0, p: 4 }}>
          <FormCancelButton onClick={onClose} />
          <LoadingButton variant="contained" type="submit" {...getSubmitButtonProps(isSubmitting, isValid)} sx={{ mr: '16px !important' }}>
            Save
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
    </Dialog>
  );
};

export default TicketCloseModal;
